import { useEffect, useState } from 'react';
import { listCountries, listNations } from '../../services/NationCountryService';
import { listCategory } from '../../services/CategoryService';
import { sortByField } from '../../utils/utils';

// Module level cache: nations / cities / categories rarely change, so every page shares
// one request instead of downloading them again on each navigation (slow on mobile data).
const cache = {};

function load(key, fetcher) {
  if (!cache[key]) {
    cache[key] = fetcher()
      .then((res) => (Array.isArray(res.data) ? res.data : []))
      .catch((error) => {
        console.error(error);
        delete cache[key];
        return [];
      });
  }
  return cache[key];
}

function useCached(key, fetcher, transform) {
  const [data, setData] = useState([]);
  useEffect(() => {
    let active = true;
    load(key, fetcher).then((list) => {
      if (active) setData(transform ? transform(list) : list);
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return data;
}

export const loadNations = () => load('nations', listNations);
export const loadCountries = () => load('countries', listCountries);

export function useNations() {
  // Skip placeholder rows such as "NO COUNTRY" (no ISO code): they cannot be searched or shown with a flag.
  return useCached('nations', listNations, (list) =>
    sortByField([...list].filter((n) => n && /^[a-z]{2}$/i.test(String(n.nationTag || '').trim())), 'nationName'));
}

export function useCountries() {
  return useCached('countries', listCountries);
}

/** Sub categories flattened with their parent category name (used by selects and details). */
export function useSubCategories() {
  return useCached('categories', listCategory, (list) =>
    list.flatMap((category) =>
      (category.subCategories || []).map((sub) => ({
        ...sub,
        categoryId: category.categoryId,
        categoryName: category.categoryName,
      }))
    )
  );
}

export function useCategories() {
  return useCached('categories', listCategory);
}

export const REMOTE_NATION = { nationId: 999, nationTag: '999', continent: 'Remote' };

export const isRemoteNation = (nation) => Boolean(nation && (nation.nationId === 999 || nation.nationTag === '999'));
